import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import AuthGuard from 'src/guards/AuthGuard';
import GuestGuard from 'src/guards/GuestGuard';
import { MobileLayout, SimpleLayout } from 'src/layouts';

import {
  AppInfo,
  Calendar,
  Care,
  CustomerService,
  DrugQuestion,
  DrugQuestionForm,
  EditMemberInfo,
  EditProfile,
  Faq,
  FindId,
  FindIdEnd,
  HomeV2,
  Inquiry,
  InquiryNew,
  LanguageSettings,
  Login,
  MedicalQuestion,
  MedicalQuestionEnd,
  MedicalQuestionForm,
  MedicineInfo,
  MedicineSettings,
  MyPage,
  Notice,
  Notification,
  NotificationSettings,
  OpenSourceLicense,
  Page403,
  Page404,
  Page500,
  PageError,
  PersonaSettings,
  PharmacySearch,
  Privacy,
  ProfileCompletion,
  Record,
  Register,
  RegisterEnd,
  Report,
  ResetPwd,
  ResetPwdEnd,
  ResetPwdForm,
  Terms,
  TermsSettings,
  Withdrawal,
} from './elements';
import { PATH_AUTH, PATH_PAGE } from './paths';

// ----------------------------------------------------------------------

const routes: RouteObject[] = [
  // ===== 404 / 에러 =====
  {
    children: [
      {
        errorElement: <PageError />,
        element: <MobileLayout />,
        children: [
          { path: PATH_PAGE.page403, element: <Page403 /> },
          { path: PATH_PAGE.page404, element: <Page404 /> },
          { path: PATH_PAGE.page500, element: <Page500 /> },
          { path: '*', element: <Navigate to={PATH_PAGE.page404} replace /> },
        ],
      },
    ],
  },
  // ===== 로그인 (SimpleLayout) =====
  {
    element: <SimpleLayout />,
    children: [
      {
        errorElement: <PageError />,
        children: [
          {
            path: PATH_AUTH.login,
            element: (
              <GuestGuard>
                <Login />
              </GuestGuard>
            ),
          },
        ],
      },
    ],
  },
  // ===== 인증/문진/마이/약물 화면 (MobileLayout · footer X) =====
  {
    element: <MobileLayout needFooter={false} />,
    children: [
      {
        errorElement: <PageError />,
        children: [
          // ----- Auth -----
          {
            path: PATH_AUTH.findId.root,
            element: (
              <GuestGuard>
                <FindId />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.findId.end,
            element: (
              <GuestGuard>
                <FindIdEnd />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.resetPwd.root,
            element: (
              <GuestGuard>
                <ResetPwd />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.resetPwd.form,
            element: (
              <GuestGuard>
                <ResetPwdForm />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.resetPwd.end,
            element: (
              <GuestGuard>
                <ResetPwdEnd />
              </GuestGuard>
            ),
          },
          // ----- 문진 (medical / drug) -----
          {
            path: PATH_PAGE.drugQuestion.root,
            element: (
              <AuthGuard>
                <DrugQuestion />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.drugQuestion.form,
            element: (
              <AuthGuard>
                <DrugQuestionForm />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.medicalQuestion.root,
            element: (
              <AuthGuard>
                <MedicalQuestion />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.medicalQuestion.form,
            element: (
              <AuthGuard>
                <MedicalQuestionForm />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.medicalQuestion.end,
            element: (
              <AuthGuard>
                <MedicalQuestionEnd />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.medicineSettings.root,
            element: (
              <AuthGuard>
                <MedicineSettings />
              </AuthGuard>
            ),
          },
          // ----- 마이 -----
          {
            path: PATH_PAGE.myPage.pharmacy,
            element: (
              <AuthGuard>
                <PharmacySearch />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.medicineInfo,
            element: (
              <AuthGuard>
                <MedicineInfo />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.privacy,
            element: (
              <AuthGuard>
                <Privacy />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.customerService.root,
            element: (
              <AuthGuard>
                <CustomerService />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.appInfo,
            element: (
              <AuthGuard>
                <AppInfo />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.openSourceLicense,
            element: (
              <AuthGuard>
                <OpenSourceLicense />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.edit.profile,
            element: (
              <AuthGuard>
                <EditProfile />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.edit.memberInfo,
            element: (
              <AuthGuard>
                <EditMemberInfo />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.terms,
            element: (
              <AuthGuard>
                <TermsSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.termsDetail,
            element: (
              <AuthGuard>
                <Terms />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.notification,
            element: (
              <AuthGuard>
                <NotificationSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.language,
            element: (
              <AuthGuard>
                <LanguageSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.settings.persona,
            element: (
              <AuthGuard>
                <PersonaSettings />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.customerService.faq,
            element: (
              <AuthGuard>
                <Faq />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.customerService.notice,
            element: (
              <AuthGuard>
                <Notice />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.customerService.inquiry,
            element: (
              <AuthGuard>
                <Inquiry />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.customerService.inquiryNew,
            element: (
              <AuthGuard>
                <InquiryNew />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.withdrawal,
            element: (
              <AuthGuard>
                <Withdrawal />
              </AuthGuard>
            ),
          },
          // ----- 기타 -----
          { path: PATH_PAGE.policy, element: <Terms /> },
          {
            path: PATH_PAGE.notification,
            element: (
              <AuthGuard>
                <Notification />
              </AuthGuard>
            ),
          },
        ],
      },
    ],
  },
  // ===== 회원가입 wizard (헤더/풋터 X — 자체 헤더 사용) =====
  {
    element: <MobileLayout needHeader={false} needFooter={false} />,
    children: [
      {
        errorElement: <PageError />,
        children: [
          {
            path: PATH_AUTH.register.root,
            element: (
              <GuestGuard>
                <Register />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.register.end,
            element: (
              <GuestGuard>
                <RegisterEnd />
              </GuestGuard>
            ),
          },
          {
            path: PATH_AUTH.profileCompletion,
            element: <ProfileCompletion />,
          },
        ],
      },
    ],
  },
  // ===== 메인 탭 (footer + floating) =====
  //
  // 5탭 화면(홈/기록/리포트/케어/마이)은 모두 자체 헤더(sage→cream gradient)를
  // 가지므로 needHeader=false. 그렇지 않으면 MUI 헤더 64px가 추가로 깔려서
  // 위쪽에 빈 띠가 생김.
  //
  // 헤더 필요한 부수 화면 (예: 검색·상세 페이지)은 별도 그룹으로 분리할 것.
  {
    children: [
      {
        errorElement: <PageError />,
        element: <MobileLayout needHeader={false} needFloating={false} />,
        children: [
          {
            path: PATH_PAGE.home,
            element: (
              <AuthGuard>
                <HomeV2 />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.record.root,
            element: (
              <AuthGuard>
                <Record />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.report.root,
            element: (
              <AuthGuard>
                <Report />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.care.root,
            element: (
              <AuthGuard>
                <Care />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.calendar.root,
            element: (
              <AuthGuard>
                <Calendar />
              </AuthGuard>
            ),
          },
          {
            path: PATH_PAGE.myPage.root,
            element: (
              <AuthGuard>
                <MyPage />
              </AuthGuard>
            ),
          },
          // ===== GLP-1 신규 (W2~W4 추가) =====
          // { path: PATH_PAGE.record?.root, element: <AuthGuard><RecordPage /></AuthGuard> },
          // { path: PATH_PAGE.report?.root, element: <AuthGuard><ReportPage /></AuthGuard> },
          // { path: PATH_PAGE.care?.root,   element: <AuthGuard><CarePage /></AuthGuard> },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes, {
  basename: '/',
  future: {
    v7_fetcherPersist: true,
    v7_relativeSplatPath: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});

export default router;
